import { useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { CHECK_PROFESSIONAL_KEY } from "@/shared/constants";
import {
  useProfessionsQuery,
  useRegistrationTypesQuery,
  useRegisterUserDataMutation,
} from "@/shared/services";
import { useModalStore, useToastStore, useUserDataStore } from "@/shared/stores";
import { type UserDataFormValues } from "../userDataForm";
import { toRegisterUserDataRequest } from "../../../mappers/registerUserData";

const CUIT_ALREADY_REGISTERED_MESSAGE =
  "El CUIT ya se encuentra registrado en la base de datos.";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const getCuitConflictMessage = (error: unknown): string | null => {
  if (!isRecord(error) || error.status !== 409) {
    return null;
  }

  const responseData = error.responseData;

  if (!isRecord(responseData) || !isRecord(responseData.data)) {
    return CUIT_ALREADY_REGISTERED_MESSAGE;
  }

  const backendMessage = responseData.data.mensajeError;

  return typeof backendMessage === "string" && backendMessage.trim()
    ? backendMessage
    : CUIT_ALREADY_REGISTERED_MESSAGE;
};

export const useUserDataModal = () => {
  const professionsQuery = useProfessionsQuery();
  const registrationTypesQuery = useRegistrationTypesQuery();
  const registerUserMutation = useRegisterUserDataMutation();
  const queryClient = useQueryClient();

  const closeModal = useModalStore((state) => state.closeModal);
  const showToast = useToastStore((state) => state.showToast);
  const setUserDataStart = useUserDataStore((state) => state.setUserDataStart);
  const setUserDataSuccess = useUserDataStore(
    (state) => state.setUserDataSuccess,
  );
  const setUserDataFailure = useUserDataStore(
    (state) => state.setUserDataFailure,
  );

  const professionOptions = useMemo(
    () =>
      professionsQuery.data?.data.professions.map((profession) => ({
        value: profession.id.toString(),
        label: profession.profession,
      })) ?? [],
    [professionsQuery.data],
  );

  const registrationTypeOptions = useMemo(
    () =>
      registrationTypesQuery.data?.data.registration_types.map(
        (registrationType) => ({
          value: registrationType.id.toString(),
          label: registrationType.registration_type,
        }),
      ) ?? [],
    [registrationTypesQuery.data],
  );

  const handleSubmit = useCallback(
    (values: UserDataFormValues) => {
      const request = toRegisterUserDataRequest(values);

      setUserDataStart();

      registerUserMutation.mutate(request, {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: [CHECK_PROFESSIONAL_KEY],
          });
          setUserDataSuccess(request.userData);
          closeModal();
        },
        onError: (error) => {
          const conflictMessage = getCuitConflictMessage(error);

          if (conflictMessage) {
            showToast({
              type: "error",
              title: conflictMessage,
              duration: 3000,
            });
          }

          setUserDataFailure(
            conflictMessage || error.message || "Registration failed",
          );
        },
      });
    },
    [
      closeModal,
      queryClient,
      registerUserMutation,
      setUserDataFailure,
      setUserDataStart,
      setUserDataSuccess,
      showToast,
    ],
  );

  return {
    closeModal,
    handleSubmit,
    isPending: registerUserMutation.isPending,
    professionOptions,
    registrationTypeOptions,
  };
};
